import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listMyDrafts, listPendingInvitations, respondToInvitation } from '../repository/draftsRepository';
import { DraftListItem, Invitation } from '../objects/draftTypes';

const DraftList: React.FC = () => {
    const [drafts, setDrafts] = useState<DraftListItem[]>([]);
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [respondingTo, setRespondingTo] = useState<string | null>(null);

    const fetchData = async () => {
        const [draftsResult, invitationsResult] = await Promise.all([
            listMyDrafts(),
            listPendingInvitations()
        ]);

        if ('error' in draftsResult) {
            setError(draftsResult.error);
        } else {
            setDrafts(draftsResult.drafts);
        }

        if (!('error' in invitationsResult)) {
            setInvitations(invitationsResult.invitations);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleInvitationResponse = async (draftUuid: string, response: 'accept' | 'decline') => {
        setRespondingTo(draftUuid);
        const result = await respondToInvitation(draftUuid, response);

        if ('error' in result) {
            setError(result.error);
        } else {
            await fetchData();
        }
        setRespondingTo(null);
    };

    const formatStatus = (status: string): string => {
        switch (status) {
            case 'in_progress':
                return 'In Progress';
            case 'waiting_for_invites':
                return 'Waiting for Invites';
            case 'completed':
                return 'Completed';
            default:
                return status;
        }
    };

    if (loading) {
        return <div className="loading">Loading drafts...</div>;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    const hasContent = drafts.length > 0 || invitations.length > 0;

    if (!hasContent) {
        return (
            <div className="empty-state">
                <p>You're not part of any drafts yet.</p>
                <Link to="/drafts/new" className="btn-primary">Create a Draft</Link>
            </div>
        );
    }

    return (
        <div className="draft-list">
            {invitations.length > 0 && (
                <div className="invitations-section">
                    <h2>Pending Invitations</h2>
                    <div className="invitation-list">
                        {invitations.map((invitation) => (
                            <div key={invitation.uuid} className="invitation-item">
                                <div className="invitation-info">
                                    <span className="draft-name">{invitation.name}</span>
                                    <span className="invitation-meta">
                                        from {invitation.owner} · {invitation.num_drafters} drafters · {invitation.num_rounds} rounds
                                    </span>
                                </div>
                                <div className="invitation-actions">
                                    <button
                                        className="btn-primary btn-small"
                                        onClick={() => handleInvitationResponse(invitation.uuid, 'accept')}
                                        disabled={respondingTo === invitation.uuid}
                                    >
                                        {respondingTo === invitation.uuid ? '...' : 'Accept'}
                                    </button>
                                    <button
                                        className="btn-secondary btn-small"
                                        onClick={() => handleInvitationResponse(invitation.uuid, 'decline')}
                                        disabled={respondingTo === invitation.uuid}
                                    >
                                        Decline
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {drafts.length > 0 && (
                <>
                    <div className="draft-list-header">
                        <h2>Your Drafts</h2>
                        <Link to="/drafts/new" className="btn-primary">New Draft</Link>
                    </div>
                    <div className="draft-list-items">
                        {drafts.map((draft) => (
                            <Link
                                key={draft.uuid}
                                to={`/drafts/${draft.uuid}`}
                                className={`draft-list-item ${draft.is_my_turn ? 'my-turn' : ''}`}
                            >
                                <div className="draft-item-main">
                                    <span className="draft-name">{draft.name}</span>
                                    {draft.is_my_turn && (
                                        <span className="turn-badge">Your turn!</span>
                                    )}
                                </div>
                                <div className="draft-item-meta">
                                    <span className={`status-badge ${draft.status}`}>
                                        {formatStatus(draft.status)}
                                    </span>
                                    <span className="draft-info">
                                        {draft.picks_made} / {draft.num_drafters * draft.num_rounds} picks
                                    </span>
                                    {draft.role === 'owner' && (
                                        <span className="owner-badge">Owner</span>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                </>
            )}

            {drafts.length === 0 && invitations.length > 0 && (
                <div className="draft-list-header">
                    <h2>Your Drafts</h2>
                    <Link to="/drafts/new" className="btn-primary">New Draft</Link>
                </div>
            )}
        </div>
    );
};

export default DraftList;
